/*
  A customer's orders on the website, the same way the app shows them.

  Loaded only by the account page section rather than by every page, because
  this is the only place it is used and the shop is mostly read on mobile
  data.

  The wording of every status is lifted from the app
  (src/shopify/customerAccountApi.ts) rather than written again, so an order
  does not read "Shipped" on a phone and "FULFILLED" on a laptop.
*/
(function () {
  'use strict';

  /* ---------- plain language ---------- */

  // Shopify's own words — VOIDED, PARTIALLY_REFUNDED — are not something a
  // customer should have to work out.
  var FINANCIAL = {
    PENDING: 'Payment Pending',
    AUTHORIZED: 'Payment Authorized',
    PARTIALLY_PAID: 'Partially Paid',
    PAID: 'Paid',
    PARTIALLY_REFUNDED: 'Partially Refunded',
    REFUNDED: 'Refunded',
    VOIDED: 'Cancelled',
    EXPIRED: 'Expired'
  };

  // FULFILLED means it has left us. Whether it has actually reached anyone
  // comes from the courier instead, in progress() below.
  var FULFILLMENT = {
    UNFULFILLED: 'Preparing Your Order',
    IN_PROGRESS: 'Preparing to Ship',
    PARTIALLY_FULFILLED: 'Partially Shipped',
    FULFILLED: 'Shipped',
    ON_HOLD: 'On Hold',
    SCHEDULED: 'Scheduled',
    OPEN: 'Preparing Your Order',
    PENDING_FULFILLMENT: 'Preparing to Ship',
    RESTOCKED: 'Preparing Your Order'
  };

  function titleCase(status) {
    return status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, ' ');
  }

  function financialStatus(status) {
    if (!status) return null;
    return FINANCIAL[status] || titleCase(status);
  }

  // The courier's own step when there is one, otherwise ours. Delhivery
  // reports every step; Bluedart reports none, so its parcels stop at
  // "Shipped" — which is the honest thing to show rather than a guess.
  function progress(order) {
    var shipment = order.shipment;
    var status = shipment && shipment.deliveredAt ? 'DELIVERED' : shipment && shipment.status;
    if (status === 'DELIVERED') return 'Delivered';
    if (status === 'OUT_FOR_DELIVERY') return 'Out for Delivery';
    if (status === 'IN_TRANSIT' || status === 'ATTEMPTED_DELIVERY') return 'On the Way';
    if (!order.fulfillmentStatus) return null;
    return FULFILLMENT[order.fulfillmentStatus] || titleCase(order.fulfillmentStatus);
  }

  /* ---------- small helpers ---------- */

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      }[ch];
    });
  }

  function rupees(amount) {
    return '₹' + Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  function shortDate(value) {
    if (!value) return '';
    return new Date(value).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }

  // Shopify resizes on its own servers, so asking for the width we actually
  // draw turns a 2000px photo into a small one before it crosses the network.
  function thumb(url, width) {
    if (!url) return '';
    return escapeHtml(url) + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  // The same number the app uses (src/config/support.ts). Anywhere this file
  // tells a customer to message us, it has to be something they can press.
  var WHATSAPP = '915653554030';

  function whatsappLink(order, text) {
    var message = text + ' (' + (order.name || '') + ')';
    return '<a class="sp-order__whatsapp" href="https://wa.me/' + WHATSAPP +
      '?text=' + encodeURIComponent(message) + '" target="_blank" rel="noopener">Message us on WhatsApp</a>';
  }

  // Shopify records the gateway under its own name. "manual" is what a COD
  // order placed by our backend carries, because the money is collected by
  // the courier and not by any gateway at all.
  var GATEWAY = {
    manual: 'Cash on Delivery',
    'cash on delivery (cod)': 'Cash on Delivery',
    cod: 'Cash on Delivery',
    razorpay: 'Razorpay',
    phonepe: 'PhonePe',
  };

  function paymentMethod(order) {
    var names = order.paymentMethods || [];
    if (!names.length) return null;
    return names.map(function (name) {
      return GATEWAY[String(name).toLowerCase()] || name;
    }).join(', ');
  }

  function addressLines(address) {
    if (!address) return [];
    var street = [address.address1, address.address2].filter(Boolean).join(', ');
    var town = [address.city, address.province, address.zip].filter(Boolean).join(', ');
    return [address.name, street, town, address.phone].filter(Boolean);
  }

  // The cart wants a plain number; everything else here speaks in gids.
  function numericId(gid) {
    var match = /(\d+)\s*$/.exec(String(gid || ''));
    return match ? match[1] : null;
  }

  /* ---------- what a customer may do ---------- */

  // Matches the backend exactly, which checks all of this again and checks
  // the order is theirs. Anything already paid or already moving needs a
  // person involved rather than a silent self-serve cancel: a Razorpay
  // payment sits outside Shopify, so cancelling the order here would not
  // return anyone's money.
  function canCancel(order) {
    return !order.cancelledAt &&
      order.fulfillmentStatus === 'UNFULFILLED' &&
      order.financialStatus !== 'PAID';
  }

  function reorderable(order) {
    return (order.lineItems || []).filter(function (line) {
      return line.variantId && numericId(line.variantId);
    });
  }

  // Goes into the website's own cart — the one Shopify keeps and the checkout
  // reads. Nothing here touches the app's cart, which is a different cart on
  // a different device.
  function addToCart(order) {
    var items = reorderable(order).map(function (line) {
      return { id: numericId(line.variantId), quantity: line.quantity };
    });
    if (!items.length) {
      return Promise.reject(new Error('Items from this order are no longer available.'));
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (json) {
        if (!response.ok) {
          // Shopify says why, and it is usually that something sold out since.
          throw new Error(json.description || json.message || 'Could not add these to your cart.');
        }
        return json;
      });
    });
  }

  /* ---------- drawing ---------- */

  function itemRow(line) {
    var image = line.imageUrl
      ? '<img class="sp-order__item-img" src="' + thumb(line.imageUrl, 120) + '" alt="" loading="lazy">'
      : '<span class="sp-order__item-img"></span>';

    // The price of the whole line, not one unit, and what it would have been
    // without the discount on it — a saving shown as a saving rather than
    // folded into a smaller number with no explanation.
    var money = '';
    if (line.price != null) {
      var saved = line.originalPrice != null && line.originalPrice > line.price;
      money = '<span class="sp-order__item-money">' +
        '<span class="sp-order__item-price">' + rupees(line.price) + '</span>' +
        (saved ? '<s class="sp-order__item-was">' + rupees(line.originalPrice) + '</s>' : '') +
      '</span>';
    }

    var inside = image +
      '<span class="sp-order__item-text">' +
        '<span class="sp-order__item-title">' + escapeHtml(line.title) + '</span>' +
        '<span class="sp-order__item-qty">Qty ' + escapeHtml(line.quantity) + '</span>' +
      '</span>' + money;

    // Tapping an item opens that poshak again — the quickest way to order the
    // same thing, or a different size of it. A product taken off the store
    // since has no handle, so that row is simply not a link, rather than a
    // link to nowhere.
    if (!line.productHandle) {
      return '<div class="sp-order__item">' + inside + '</div>';
    }
    return '<a class="sp-order__item" href="/products/' + encodeURIComponent(line.productHandle) + '">' +
      inside +
      '<span class="sp-order__item-go" aria-hidden="true">&rsaquo;</span>' +
    '</a>';
  }

  // A tracking number worth showing has a digit in it. Orders fulfilled by
  // hand here often carry a word instead — "done" — and "Other" is Shopify's
  // placeholder for a courier it does not recognise, so both together read as
  // "Other - done", which tells a customer nothing and looks broken.
  function trackingNote(tracking) {
    if (!tracking || !tracking.number || !/d/.test(tracking.number)) return '';
    var company = tracking.company && tracking.company !== 'Other' ? tracking.company : null;
    return '<div class="sp-order__tracking">' +
      (company ? escapeHtml(company) + ' &middot; ' : '') +
      '<strong>' + escapeHtml(tracking.number) + '</strong>' +
    '</div>';
  }

  function sumRow(label, value, kind) {
    return '<div class="sp-order__sum' + (kind ? ' sp-order__sum--' + kind : '') + '">' +
      '<span>' + label + '</span><span>' + value + '</span></div>';
  }

  function detailsPanel(order) {
    var html = trackingNote(order.tracking);

    html += '<div class="sp-order__items">' +
      (order.lineItems || []).map(itemRow).join('') +
    '</div>';

    // Added up from the lines listed just above, not from Shopify's own
    // subtotal. Shopify's subtotal is already AFTER the discount, so printing
    // it above a "Discount" line made one of Rajesh's orders read
    // "Subtotal 833, Discount -44, Total 833" — which does not add up and
    // reads as the shop getting its own arithmetic wrong.
    var lines = order.lineItems || [];
    var priced = lines.length > 0 && lines.every(function (line) { return line.price != null; });
    var itemsTotal = priced
      ? lines.reduce(function (sum, line) { return sum + line.price; }, 0)
      : null;
    var discount = order.discount || 0;
    var shipping = order.shipping || 0;

    // Shown only when it comes to the total we are also showing. A breakdown
    // that does not add up is worse than no breakdown, and an order placed
    // before the backend sent any of this has none of it — its total is
    // still right, so that is all it gets.
    var addsUp = itemsTotal !== null &&
      Math.abs(itemsTotal - discount + shipping - order.totalPrice) < 1;

    var sums = '';
    if (addsUp) {
      sums += sumRow('Items', rupees(itemsTotal));
      if (discount > 0) sums += sumRow('Discount', '−' + rupees(discount), 'save');
      if (order.shipping != null) {
        sums += sumRow('Delivery', shipping > 0 ? rupees(shipping) : 'Free');
      }
    }
    sums += sumRow('Total', '<strong>' + rupees(order.totalPrice) + '</strong>', 'total');
    html += '<div class="sp-order__sums">' + sums + '</div>';

    var paid = paymentMethod(order);
    if (paid) html += '<div class="sp-order__pay">Paid by ' + escapeHtml(paid) + '</div>';

    var address = addressLines(order.shippingAddress);
    if (address.length) {
      html += '<div class="sp-order__ship">' +
        '<div class="sp-order__ship-label">Delivery address</div>' +
        address.map(escapeHtml).join('<br>') +
      '</div>';
    }

    if (order.cancelledAt) {
      html += '<p class="sp-order__note">This order was cancelled on ' +
        escapeHtml(shortDate(order.cancelledAt)) + '.</p>';
    } else if (canCancel(order)) {
      html += '<button class="sp-order__cancel" type="button" data-action="cancel">Cancel this order</button>';
    } else {
      var why = order.financialStatus === 'PAID'
        ? 'This order is already paid, so cancelling it needs a refund arranging.'
        : 'This order has already started shipping, so we have to cancel it for you.';
      html += '<p class="sp-order__note">' + why + '</p>' +
        whatsappLink(order, order.financialStatus === 'PAID'
          ? 'Hello, I would like to cancel my order and arrange a refund.'
          : 'Hello, I would like to cancel my order.');
    }

    return '<div class="sp-order__details" data-details hidden>' + html + '</div>';
  }

  function orderCard(order, index) {
    var lines = order.lineItems || [];
    var first = lines[0];
    var extra = lines.length - 1;
    var cancelled = !!order.cancelledAt;
    var step = progress(order);
    var paid = financialStatus(order.financialStatus);

    var badge = '';
    if (cancelled) {
      badge = '<span class="sp-order__badge sp-order__badge--cancelled">Cancelled</span>';
    } else if (step) {
      var done = step === 'Delivered' || step === 'Shipped' || step === 'Out for Delivery';
      badge = '<span class="sp-order__badge' + (done ? ' sp-order__badge--shipped' : '') + '">' +
        escapeHtml(step) + '</span>';
    }

    var actions = '';
    // A cancelled order has nothing in transit, so there is nothing to track.
    if (!cancelled && order.tracking && order.tracking.url) {
      actions += '<a class="sp-order__action" href="' + escapeHtml(order.tracking.url) + '"' +
        ' target="_blank" rel="noopener">Track order' +
        (order.tracking.company ? ' (' + escapeHtml(order.tracking.company) + ')' : '') +
      '</a>';
    }
    if (reorderable(order).length) {
      actions += '<button class="sp-order__action sp-order__action--primary" type="button"' +
        ' data-action="reorder">Order again</button>';
    }
    actions += '<button class="sp-order__action" type="button" data-action="details"' +
      ' aria-expanded="false">View details</button>';

    return '<div class="sp-order" data-order="' + index + '">' +
      '<div class="sp-order__head">' +
        (first && first.imageUrl
          ? '<img class="sp-order__thumb" src="' + thumb(first.imageUrl, 160) + '" alt="" loading="lazy">'
          : '<span class="sp-order__thumb"></span>') +
        '<div class="sp-order__main">' +
          '<div class="sp-order__top">' +
            '<span class="sp-order__name">' + escapeHtml(order.name || '') + '</span>' +
            '<span class="sp-order__price">' + rupees(order.totalPrice) + '</span>' +
          '</div>' +
          '<div class="sp-order__date">' + escapeHtml(shortDate(order.processedAt)) +
            (paid ? ' &middot; ' + escapeHtml(paid) : '') +
          '</div>' +
          (first
            ? '<div class="sp-order__summary">' + escapeHtml(first.title) +
              (extra > 0 ? ' +' + extra + ' more' : '') + '</div>'
            : '') +
          badge +
        '</div>' +
      '</div>' +
      '<div class="sp-order__actions">' + actions + '</div>' +
      '<p class="sp-order__error" data-error hidden></p>' +
      detailsPanel(order) +
    '</div>';
  }

  /* ---------- the section as a whole ---------- */

  // Someone who shops here often has dozens of orders, and the ones that
  // matter are the ones they just placed. The rest is history, and history
  // should not push everything else off the bottom of the screen.
  var SHOWN_AT_FIRST = 2;

  function render(box, orders) {
    if (!orders.length) {
      box.innerHTML = '<p class="sp-account__muted">No orders yet.</p>';
      return;
    }

    var cards = orders.map(orderCard);

    box.innerHTML = cards.slice(0, SHOWN_AT_FIRST).join('') +
      (cards.length > SHOWN_AT_FIRST
        ? '<div data-older hidden>' + cards.slice(SHOWN_AT_FIRST).join('') + '</div>' +
          '<button class="sp-account__more" type="button" data-action="see-all">' +
            'See all ' + orders.length + ' orders' +
          '</button>'
        : '');

    // One listener for the whole list, the orders still hidden behind
    // "See all" included, so nothing has to be wired up again when they
    // appear. They were already fetched, so showing them is instant.
    box.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-action]');
      if (!trigger || !box.contains(trigger)) return;

      var action = trigger.getAttribute('data-action');

      if (action === 'see-all') {
        box.querySelector('[data-older]').hidden = false;
        trigger.remove();
        return;
      }

      var card = trigger.closest('[data-order]');
      if (!card) return;

      var order = orders[Number(card.getAttribute('data-order'))];
      var errorBox = card.querySelector('[data-error]');

      function fail(message) {
        errorBox.textContent = message;
        errorBox.hidden = false;
      }

      if (action === 'details') {
        var panel = card.querySelector('[data-details]');
        panel.hidden = !panel.hidden;
        trigger.setAttribute('aria-expanded', panel.hidden ? 'false' : 'true');
        trigger.textContent = panel.hidden ? 'View details' : 'Hide details';
        return;
      }

      if (action === 'reorder') {
        errorBox.hidden = true;
        trigger.disabled = true;
        trigger.textContent = 'Adding…';
        addToCart(order).then(function () {
          // Straight to the cart, the way the app goes straight to its own,
          // so the next step is in front of the customer rather than hidden
          // behind a number in the header.
          window.location.href = '/cart';
        }).catch(function (err) {
          trigger.disabled = false;
          trigger.textContent = 'Order again';
          fail(err.message);
        });
        return;
      }

      if (action === 'cancel') {
        if (!window.confirm(order.name + ' will be cancelled. This cannot be undone.')) return;
        errorBox.hidden = true;
        trigger.disabled = true;
        trigger.textContent = 'Cancelling…';
        window.SP.cancelOrder(order.id).then(function () {
          // Read back from the server rather than patched into the page, so
          // what is shown is what actually happened.
          window.location.reload();
        }).catch(function (err) {
          trigger.disabled = false;
          trigger.textContent = 'Cancel this order';
          fail(err.message);
        });
      }
    });
  }

  window.SPOrders = { render: render };
})();
